import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import NavBar from "../../Components/navbar/navbar";
import Footer from "../../Components/footer/footer";
import { IoIosArrowDown } from "react-icons/io";
import { MdLocationOn, MdCalendarToday, MdOpenInNew } from "react-icons/md";
import "./calendar.css";
import ScrollToTop from "../../Components/scrollToTop/scrollToTop";
import axios from "axios";

function Calendar() {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchEvent, setSearchEvent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPastEvents, setShowPastEvents] = useState(false);

  const handleScroll = (e) => {
    e.preventDefault();
    const section = document.querySelector(".ourCalendar");
    if (section) {
      window.scrollTo({
        top: section.offsetTop,
        behavior: "smooth",
      });
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchEvent(value);
    filterEvents(value);
  };

  const filterEvents = (searchTerm, sourceEvents = events) => {
    const filtered = sourceEvents.filter((event) =>
      event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.event_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.state.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEvents(filtered);
  };

  const getDateObj = (dateString) => {
    try {
      const [year, month, day] = dateString.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      const monthNames = [
        "ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO",
        "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"
      ];
      return {
        day: day,
        month: monthNames[month - 1] || "MES",
        year: year,
        fullDate: date
      };
    } catch (e) {
      return {
        day: 1,
        month: "MES",
        year: 2026,
        fullDate: new Date()
      };
    }
  };

  const isCurrentOrFutureMonth = (dateString) => {
    const { fullDate } = getDateObj(dateString);
    const now = new Date();
    const eventYear = fullDate.getFullYear();
    const eventMonth = fullDate.getMonth();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    return (
      eventYear > currentYear ||
      (eventYear === currentYear && eventMonth >= currentMonth)
    );
  };

  const splitEventsByPeriod = (eventList) => {
    const upcoming = [];
    const past = [];
    eventList.forEach((event) => {
      if (isCurrentOrFutureMonth(event.start_date)) {
        upcoming.push(event);
      } else {
        past.push(event);
      }
    });
    return { upcoming, past };
  };

  useEffect(() => {
    getCalendarEvents();
  }, []);

  const getCalendarEvents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL_BACK}/0a2b15b1a0d8d09e86d4b42d2012a091/calendars`
      );

      if (response.data.data) {
        const sortedEvents = response.data.data.sort((a, b) => {
          return new Date(a.start_date) - new Date(b.start_date);
        });
        setEvents(sortedEvents);
        setFilteredEvents(sortedEvents);
      }
      setError(null);
    } catch (error) {
      console.log(error);
      setError("No se pudieron cargar los eventos");
    } finally {
      setLoading(false);
    }
  };

  const handleInputsClear = () => {
    setSearchEvent("");
    setFilteredEvents(events);
  };

  const groupEventsByMonth = (eventList) => {
    const grouped = {};
    eventList.forEach((event) => {
      const start = getDateObj(event.start_date);
      const monthYear = `${start.month} ${start.year}`;
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(event);
    });
    return grouped;
  };

  const { upcoming: upcomingEvents, past: pastEvents } =
    splitEventsByPeriod(filteredEvents);
  const groupedUpcomingEvents = groupEventsByMonth(upcomingEvents);
  const groupedPastEvents = groupEventsByMonth(pastEvents);

  const getEventTagColors = (eventType) => {
    switch (eventType?.toLowerCase()) {
      case "nacional":
        return { bg: "#FFC726", text: "#000000" };
      case "regional":
        return { bg: "#E5239D", text: "#FFFFFF" };
      case "estatal":
        return { bg: "#00B5E6", text: "#FFFFFF" };
      default:
        return { bg: "#003366", text: "#FFFFFF" };
    }
  };

  const renderEventsTimeline = (grouped, variant = "upcoming") => (
    Object.entries(grouped).map(([monthYear, monthEvents]) => (
      <div key={monthYear} className={`events-month-group ${variant === "past" ? "events-month-group--past" : ""}`}>
        <h3 className="month-title">{monthYear}</h3>
        <div className="events-list">
          {monthEvents.map((event) => {
            const start = getDateObj(event.start_date);
            const end = event.end_date ? getDateObj(event.end_date) : null;

            let dateRangeText = "";
            if (!end || event.start_date === event.end_date) {
              dateRangeText = `${start.day} de ${start.month.toLowerCase()}`;
            } else if (start.month === end.month) {
              dateRangeText = `${start.day} - ${end.day} de ${start.month.toLowerCase()}`;
            } else {
              dateRangeText = `${start.day} de ${start.month.toLowerCase()} - ${end.day} de ${end.month.toLowerCase()}`;
            }

            return (
              <div key={event.id} className="event-card">
                <div className="event-date-badge">
                  <div className="badge-day">{start.day}</div>
                  <div className="badge-month">{start.month.substring(0, 3)}</div>
                </div>

                <div className="event-dot"></div>

                <div className="event-content">
                  <h4 className="event-title">{event.name}</h4>

                  <div className="event-meta">
                    {(() => {
                      const colors = getEventTagColors(event.event_type);
                      return (
                        <span
                          className="event-type-tag"
                          style={{
                            backgroundColor: colors.bg,
                            color: colors.text,
                          }}
                        >
                          {event.event_type}
                        </span>
                      );
                    })()}
                  </div>

                  <div className="event-details">
                    <div className="detail-row">
                      <MdLocationOn className="detail-icon" />
                      <div className="detail-text">
                        <p className="detail-label">
                          {event.state}, {event.country}
                        </p>
                        {event.venue && (
                          <p
                            className={`detail-sublabel ${event.venue.toLowerCase().includes("por definir") ? "venue-pending" : ""}`}
                          >
                            {event.venue}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="detail-row">
                      <MdCalendarToday className="detail-icon" />
                      <p className="detail-text">{dateRangeText}</p>
                    </div>
                  </div>

                  {event.address && (
                    <div
                      className={`event-address ${event.address.toLowerCase().includes("por definir") ? "address-pending" : ""}`}
                    >
                      <p>{event.address}</p>
                    </div>
                  )}

                  <div className="event-actions">
                    {event.g_maps_url && (
                      <a
                        href={event.g_maps_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="maps-link"
                      >
                        <MdOpenInNew className="maps-icon" />
                        Ver en Google Maps
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ))
  );

  return (
    <>
      <ScrollToTop />
      <Helmet>
        <title>Calendario | WA México</title>
        <meta name="description" content="Calendario de eventos y competencias de World Archery México." />
      </Helmet>
      <NavBar />
      <section className="contOurAthletes">
        <div className="athletes-title">
          <h1>Calendario de Eventos</h1>
        </div>

        <div className="contentParrafoAthletes">
          <p>
            Consulta el calendario de competencias y eventos de World Archery México.
          </p>
        </div>

        <div>
          <a
            href="#"
            className="scroll-down"
            address="true"
            onClick={handleScroll}
          >
            <IoIosArrowDown className="scroll-down-arrow" />
          </a>
        </div>
      </section>

      <div className="container p-5">
        <section className="ourCalendar" id="ourCalendar">
          <div className="our_Athletes_title">
            <h2>Eventos Próximos</h2>
          </div>
        </section>

        <div className="events-timeline">
          {loading ? (
            <div className="loading-message">
              <p>Cargando eventos...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
            </div>
          ) : upcomingEvents.length === 0 && pastEvents.length === 0 ? (
            <div className="no-events-message">
              <p>No hay eventos disponibles</p>
            </div>
          ) : (
            <>
              {upcomingEvents.length === 0 ? (
                <div className="no-events-message no-events-message--inline">
                  <p>No hay eventos programados para este mes en adelante.</p>
                </div>
              ) : (
                renderEventsTimeline(groupedUpcomingEvents)
              )}

              {pastEvents.length > 0 && (
                <div className="past-events-section">
                  <button
                    type="button"
                    className="past-events-toggle"
                    onClick={() => setShowPastEvents((prev) => !prev)}
                    aria-expanded={showPastEvents}
                  >
                    {showPastEvents
                      ? "Ocultar eventos de meses anteriores"
                      : `Ver eventos de meses anteriores (${pastEvents.length})`}
                  </button>

                  {showPastEvents && (
                    <div className="past-events-content">
                      <h3 className="past-events-heading">Eventos anteriores</h3>
                      {renderEventsTimeline(groupedPastEvents, "past")}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
}

export default Calendar;
